import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface PaymentGateway {
  id: number;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  is_active: boolean;
  configured: boolean;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PaymentGatewaysResponse {
  success: boolean;
  gateways: PaymentGateway[];
  count: number;
}

export interface UpdateGatewayPayload {
  code: string;
  status: 'active' | 'inactive';
}

export interface UpdateGatewayResponse {
  success: boolean;
  message: string;
  gateway: PaymentGateway;
}

interface PaymentGatewaysState {
  gateways: PaymentGateway[];
  loading: boolean;
  error: string | null;
  count: number;
  updatingGateway: boolean;
  updateError: string | null;
}

const initialState: PaymentGatewaysState = {
  gateways: [],
  loading: false,
  error: null,
  count: 0,
  updatingGateway: false,
  updateError: null,
};

export const fetchPaymentGateways = createAsyncThunk(
  'paymentGateways/fetchPaymentGateways',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/payment-gateways/');
      const data = response.data as PaymentGatewaysResponse;
      
      if (!data.success) {
        return rejectWithValue('Failed to fetch payment gateways');
      }
      
      return data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to fetch payment gateways';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateGatewayStatus = createAsyncThunk(
  'paymentGateways/updateGatewayStatus',
  async (payload: UpdateGatewayPayload, { rejectWithValue }) => {
    try {
      const response = await api.patch('/admin/payment-gateways/', payload);
      const data = response.data as UpdateGatewayResponse;
      
      if (!data.success) {
        return rejectWithValue(data.message || 'Failed to update payment gateway');
      }
      
      return data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to update payment gateway';
      return rejectWithValue(errorMessage);
    }
  }
);

const paymentGatewaysSlice = createSlice({
  name: 'paymentGateways',
  initialState,
  reducers: {
    clearPaymentGatewaysError: (state) => {
      state.error = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentGateways.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.gateways = action.payload.gateways || [];
        state.count = action.payload.count || 0;
      })
      .addCase(fetchPaymentGateways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.gateways = [];
        state.count = 0;
      })
      .addCase(updateGatewayStatus.pending, (state) => {
        state.updatingGateway = true;
        state.updateError = null;
      })
      .addCase(updateGatewayStatus.fulfilled, (state, action) => {
        state.updatingGateway = false;
        state.updateError = null;
        // Update the gateway in the gateways array
        const updatedGateway = action.payload.gateway;
        const index = state.gateways.findIndex(g => g.code === updatedGateway.code);
        if (index !== -1) {
          state.gateways[index] = updatedGateway;
        }
      })
      .addCase(updateGatewayStatus.rejected, (state, action) => {
        state.updatingGateway = false;
        state.updateError = action.payload as string;
      });
  },
});

export const { clearPaymentGatewaysError } = paymentGatewaysSlice.actions;
export default paymentGatewaysSlice.reducer;

